@file:androidx.compose.runtime.NoLiveLiterals

import kotlinx.browser.document
import kotlinx.coroutines.suspendCancellableCoroutine
import org.w3c.dom.HTMLImageElement
import org.w3c.files.Blob
import org.w3c.files.File
import kotlin.coroutines.resume

private const val MAX_DIMENSION = 1600
private const val JPEG_QUALITY = 0.85

// window.URL, accessed dynamically -- Kotlin/JS's org.w3c.dom.url.URL binding doesn't reliably
// expose createObjectURL/revokeObjectURL across compiler versions, and this needs nothing else.
private val urlApi: dynamic get() = js("URL")

/**
 * Downscales a picked match-protocol photo on the client before upload (no server-side image
 * library in this stack — see spec 10.9): draws it into an offscreen canvas capped at
 * MAX_DIMENSION on its longer side and re-encodes as JPEG. Runs unconditionally (even on an
 * already-small image) so every upload ends up as a consistent `.jpg`, matching the fixed
 * `$matchId.jpg` storage path. Falls back to the original file if the browser can't decode it.
 */
suspend fun resizeImageForUpload(file: File): Blob {
    val objectUrl = urlApi.createObjectURL(file) as String
    try {
        val img = document.createElement("img") as HTMLImageElement
        val loaded = suspendCancellableCoroutine<Boolean> { cont ->
            img.asDynamic().onload = { cont.resume(true) }
            img.asDynamic().onerror = { cont.resume(false) }
            img.src = objectUrl
        }
        if (!loaded) return file

        val naturalWidth = img.asDynamic().naturalWidth as Int
        val naturalHeight = img.asDynamic().naturalHeight as Int
        val scale = (MAX_DIMENSION.toDouble() / maxOf(naturalWidth, naturalHeight)).coerceAtMost(1.0)
        val targetW = (naturalWidth * scale).toInt().coerceAtLeast(1)
        val targetH = (naturalHeight * scale).toInt().coerceAtLeast(1)

        val canvas = document.createElement("canvas").asDynamic()
        canvas.width = targetW
        canvas.height = targetH
        val ctx = canvas.getContext("2d")
        ctx.drawImage(img, 0, 0, targetW, targetH)

        val blob = suspendCancellableCoroutine<Blob?> { cont ->
            canvas.toBlob({ b: Blob? -> cont.resume(b) }, "image/jpeg", JPEG_QUALITY)
        }
        return blob ?: file
    } finally {
        urlApi.revokeObjectURL(objectUrl)
    }
}
